import { useLogoStore, type Layout, type ColorCategory, type OnboardingPrefs, type MyTemplate, type ContainerShape, type IconPattern, type TitleStyle3D, type LogoFrame } from "../state/logoStore";
import { templateToRenderState } from "../engine/templateToRenderState";
import { ICONS } from "../assets/icons";
import { PageLabel } from "../components/ui/PageLabel";
import { renderLogoSVG } from "../engine/render";
import { generateVariations } from "../engine/colorExtractor";
import { scoreLogo } from "../engine/aestheticScore";

const INDUSTRY_ICON_BIAS: Record<string, string[]> = {
  tech:        ["spark", "hex", "bolt", "globe", "infinity", "ring"],
  health:      ["heart", "shield", "drop", "leaf", "ring"],
  food:        ["leaf", "heart", "drop", "tree", "flame"],
  beauty:      ["heart", "spark", "diamond", "ring", "moon"],
  finance:     ["shield", "diamond", "hex", "ring"],
  fitness:     ["bolt", "flame", "star", "infinity"],
  retail:      ["diamond", "spark", "star", "heart"],
  "real-estate": ["shield", "tree", "hex", "ring"],
  education:   ["star", "tree", "shield", "hex"],
  creative:    ["spark", "diamond", "infinity", "wave", "moon"],
  consulting:  ["hex", "shield", "diamond", "ring"],
  travel:      ["globe", "wave", "moon", "drop"],
  pets:        ["heart", "drop", "leaf"],
  photo:       ["spark", "diamond", "ring", "moon"],
  auto:        ["bolt", "shield", "hex", "spark"],
  other:       [],
};

const COLOR_HUE_RANGES: Record<ColorCategory, [number, number]> = {
  blue:      [200, 250],
  purple:    [260, 290],
  pink:      [300, 340],
  red:       [350, 360],
  orange:    [15,  40],
  yellow:    [45,  65],
  green:     [90,  150],
  teal:      [160, 195],
  greyscale: [-1,  -1], // sentinel — match low-saturation
};

function hexToHSL(hex: string): { h: number; s: number; l: number } {
  const s = hex.replace("#", "");
  const r = parseInt(s.slice(0, 2), 16) / 255;
  const g = parseInt(s.slice(2, 4), 16) / 255;
  const b = parseInt(s.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h = h * 60;
  if (h < 0) h += 360;
  return { h, s: sat, l };
}

function colorMatchesPreference(hex: string, prefs: ColorCategory[]): boolean {
  if (prefs.length === 0) return false;
  const hsl = hexToHSL(hex);
  for (const pref of prefs) {
    if (pref === "greyscale") {
      if (hsl.s < 0.15) return true;
    } else {
      const [lo, hi] = COLOR_HUE_RANGES[pref];
      if (hsl.s > 0.15 && hsl.h >= lo && hsl.h <= hi) return true;
    }
  }
  return false;
}

// Tag → icon ids it boosts (matches the symbol types shown on the wizard)
const TAG_TO_ICONS: Record<string, string[]> = {
  Heart: ["heart"], Health: ["heart", "leaf", "shield"], Healthcare: ["heart", "shield"],
  Medical: ["heart", "shield"], Medicine: ["drop", "shield"], Pharmacy: ["drop"],
  Hospital: ["shield", "heart"], Wellness: ["leaf", "heart"], Vision: ["eye", "ring"],
  Leaf: ["leaf", "tree"], Star: ["star"], Shield: ["shield"],
  Sustainability: ["leaf", "tree", "drop"], Innovation: ["spark", "infinity", "bolt"],
  Strength: ["shield", "bolt"], Excellence: ["star", "diamond"],
  Tranquility: ["moon", "leaf", "wave"], Creativity: ["spark", "diamond", "infinity"],
  Tech: ["spark", "hex", "bolt"], Code: ["hex"], Network: ["globe", "infinity"],
  Cloud: ["wave", "drop"], Speed: ["bolt"], AI: ["spark", "infinity"],
  Food: ["leaf", "drop"], Restaurant: ["leaf"], Cafe: ["heart"], Bakery: ["heart", "drop"],
  Organic: ["leaf"], Fresh: ["leaf", "drop"], Beauty: ["heart", "diamond"],
  Spa: ["leaf", "drop"], Salon: ["spark"], Diamond: ["diamond"],
  Finance: ["shield", "diamond"], Money: ["diamond"], Trust: ["shield"],
  Investment: ["diamond", "infinity"], Banking: ["shield"],
  Fitness: ["bolt", "flame"], Energy: ["bolt", "spark"], Bolt: ["bolt"],
  Flame: ["flame"], Power: ["bolt", "flame"],
  Property: ["shield", "hex"], Home: ["shield"], Architecture: ["hex"],
  Education: ["star", "tree"], Learning: ["tree", "star"], Knowledge: ["star"],
  Growth: ["tree", "leaf"],
  Creative: ["spark", "infinity"], Design: ["diamond"], Inspiration: ["spark", "star"],
  Consulting: ["hex", "shield"], Strategy: ["hex"],
  Travel: ["globe", "wave"], Globe: ["globe"], Wave: ["wave"], Adventure: ["bolt", "globe"],
  Pets: ["heart"], Care: ["heart"],
  Photography: ["spark", "ring"], Lens: ["ring"], Light: ["spark"],
  Auto: ["bolt", "shield"],
  Abstract: ["hex", "spark", "infinity", "diamond", "ring"],
  Quality: ["diamond", "star"],
};

function onboardingBonus(t: { iconId: string; titleColor: string; iconColor: string; background: string }, prefs: OnboardingPrefs): number {
  let bonus = 0;
  if (prefs.industry) {
    const goodIcons = INDUSTRY_ICON_BIAS[prefs.industry] || [];
    if (goodIcons.includes(t.iconId)) bonus += 0.08;
  }
  if (prefs.symbolTypes && prefs.symbolTypes.length > 0) {
    for (const tag of prefs.symbolTypes) {
      const icons = TAG_TO_ICONS[tag];
      if (icons && icons.includes(t.iconId)) { bonus += 0.06; break; }
    }
  }
  if (prefs.colorPreferences.length > 0) {
    if (colorMatchesPreference(t.iconColor, prefs.colorPreferences)) bonus += 0.05;
    if (colorMatchesPreference(t.titleColor, prefs.colorPreferences)) bonus += 0.04;
    if (colorMatchesPreference(t.background, prefs.colorPreferences)) bonus += 0.03;
  }
  return bonus;
}

interface Template {
  id: string;
  iconId: string;
  layout: Layout;
  titleFont: string;
  sloganFont: string;
  titleColor: string;
  sloganColor: string;
  iconColor: string;
  background: string;
  // Optional extended fields — comic presets use these to set up the full
  // container/pattern/3D-text bundle in one click. Omitted fields fall back
  // to the editor's current style values, so the standard templates above
  // keep their existing minimal behaviour.
  containerShape?: ContainerShape;
  containerColor?: string;
  containerColor2?: string;
  containerPattern?: IconPattern;
  containerPatternColor?: string;
  iconPattern?: IconPattern;
  iconPatternColor?: string;
  titleStyle3D?: TitleStyle3D;
  titleStroke3D?: number;
  titleSkew?: number;
  logoFrame?: LogoFrame;
  logoFrameColor?: string;
  logoFrameBorderColor?: string;
  logoFrameBorderWidth?: number;
}

const TEMPLATES: Template[] = [
  { id: "t1", iconId: "spark",   layout: "icon-top",   titleFont: "Montserrat",       sloganFont: "Inter",        titleColor: "#0f172a", sloganColor: "#64748b", iconColor: "#3b82f6", background: "#ffffff" },
  { id: "t2", iconId: "leaf",    layout: "icon-left",  titleFont: "Playfair Display", sloganFont: "Lora",         titleColor: "#14532d", sloganColor: "#65a30d", iconColor: "#16a34a", background: "#f0fdf4" },
  { id: "t3", iconId: "bolt",    layout: "icon-top",   titleFont: "Bebas Neue",       sloganFont: "Inter",        titleColor: "#facc15", sloganColor: "#fafafa", iconColor: "#facc15", background: "#0f172a" },
  { id: "t4", iconId: "drop",    layout: "icon-top",   titleFont: "Raleway",          sloganFont: "Raleway",      titleColor: "#0c4a6e", sloganColor: "#0284c7", iconColor: "#0ea5e9", background: "#f0f9ff" },
  { id: "t5", iconId: "heart",   layout: "icon-left",  titleFont: "Pacifico",         sloganFont: "Caveat",       titleColor: "#be185d", sloganColor: "#9333ea", iconColor: "#ec4899", background: "#fdf2f8" },
  { id: "t6", iconId: "shield",  layout: "icon-top",   titleFont: "Oswald",           sloganFont: "Inter",        titleColor: "#1e293b", sloganColor: "#475569", iconColor: "#1e293b", background: "#f1f5f9" },
  { id: "t7", iconId: "globe",   layout: "icon-left",  titleFont: "Poppins",          sloganFont: "Poppins",      titleColor: "#1e3a8a", sloganColor: "#3b82f6", iconColor: "#2563eb", background: "#eff6ff" },
  { id: "t8", iconId: "hex",     layout: "icon-top",   titleFont: "JetBrains Mono",   sloganFont: "JetBrains Mono", titleColor: "#0c0a09", sloganColor: "#78716c", iconColor: "#f97316", background: "#fafaf9" },
  { id: "t9", iconId: "infinity", layout: "icon-top",  titleFont: "Anton",            sloganFont: "Inter",        titleColor: "#7c2d12", sloganColor: "#c2410c", iconColor: "#ea580c", background: "#fff7ed" },
  { id: "t10", iconId: "moon",   layout: "icon-left",  titleFont: "Merriweather",     sloganFont: "Lora",         titleColor: "#fafafa", sloganColor: "#a1a1aa", iconColor: "#fde68a", background: "#18181b" },
  { id: "t11", iconId: "tree",   layout: "icon-top",   titleFont: "Lora",             sloganFont: "Lora",         titleColor: "#365314", sloganColor: "#65a30d", iconColor: "#4d7c0f", background: "#f7fee7" },
  { id: "t12", iconId: "diamond",layout: "icon-top",   titleFont: "Playfair Display", sloganFont: "Inter",        titleColor: "#1e1b4b", sloganColor: "#6366f1", iconColor: "#6366f1", background: "#eef2ff" },

  // ============================================================
  // Comic-book presets — bold uppercase, hard shadows, halftone +
  // burst + speech-bubble + sticker containers.
  // Palette: yellow #FAF72F, red #F85059, blue #62B7ED, navy #30226C, cyan #E3F1F5.
  // ============================================================
  {
    id: "comic-shield",
    iconId: "shield",
    layout: "icon-top",
    titleFont: "Bebas Neue",
    sloganFont: "Inter",
    titleColor: "#FAF72F",
    sloganColor: "#111111",
    iconColor: "#F85059",
    background: "#62B7ED",
    containerShape: "speech-bubble",
    containerColor: "#62B7ED",
    iconPattern: "halftone-fade",
    iconPatternColor: "#111111",
    titleStyle3D: "comic",
    titleStroke3D: 4,
    titleSkew: -2,
    logoFrame: "rounded",
    logoFrameColor: "#ffffff",
    logoFrameBorderColor: "#111111",
    logoFrameBorderWidth: 6,
  },
  {
    id: "comic-burst",
    iconId: "bolt",
    layout: "icon-top",
    titleFont: "Anton",
    sloganFont: "Inter",
    titleColor: "#62B7ED",
    sloganColor: "#FAF72F",
    iconColor: "#FAF72F",
    background: "#30226C",
    containerShape: "burst",
    containerColor: "#FAF72F",
    containerPattern: "starburst",
    containerPatternColor: "#111111",
    titleStyle3D: "comic",
    titleStroke3D: 4,
    logoFrame: "rounded",
    logoFrameColor: "#30226C",
    logoFrameBorderColor: "#111111",
    logoFrameBorderWidth: 6,
  },
  {
    id: "comic-sticker",
    iconId: "star",
    layout: "icon-top",
    titleFont: "Bebas Neue",
    sloganFont: "Inter",
    titleColor: "#F85059",
    sloganColor: "#111111",
    iconColor: "#FAF72F",
    background: "#E3F1F5",
    containerShape: "sticker",
    containerColor: "#FAF72F",
    iconPattern: "dots-large",
    iconPatternColor: "#111111",
    titleStyle3D: "comic",
    titleStroke3D: 4,
    logoFrame: "rounded",
    logoFrameColor: "#E3F1F5",
    logoFrameBorderColor: "#111111",
    logoFrameBorderWidth: 6,
  },
  {
    id: "comic-bubble",
    iconId: "infinity",
    layout: "icon-top",
    titleFont: "Anton",
    sloganFont: "Inter",
    titleColor: "#FAF72F",
    sloganColor: "#FAF72F",
    iconColor: "#62B7ED",
    background: "#F85059",
    containerShape: "speech-bubble",
    containerColor: "#ffffff",
    containerPattern: "speed-lines",
    containerPatternColor: "#111111",
    titleStyle3D: "comic",
    titleStroke3D: 4,
    titleSkew: -2,
    logoFrame: "rounded",
    logoFrameColor: "#F85059",
    logoFrameBorderColor: "#111111",
    logoFrameBorderWidth: 6,
  },
];

// Pair extracted-color variations with template "shells" (icon + layout + fonts) so they look complete
const PALETTE_SHELLS: Pick<Template, "iconId" | "layout" | "titleFont" | "sloganFont">[] = [
  { iconId: "spark",    layout: "icon-top",   titleFont: "Montserrat",       sloganFont: "Inter" },
  { iconId: "diamond",  layout: "icon-top",   titleFont: "Playfair Display", sloganFont: "Inter" },
  { iconId: "hex",      layout: "icon-left",  titleFont: "Bebas Neue",       sloganFont: "Inter" },
  { iconId: "drop",     layout: "icon-top",   titleFont: "Raleway",          sloganFont: "Raleway" },
  { iconId: "shield",   layout: "icon-top",   titleFont: "Oswald",           sloganFont: "Inter" },
  { iconId: "leaf",     layout: "icon-left",  titleFont: "Lora",             sloganFont: "Lora" },
  { iconId: "bolt",     layout: "icon-top",   titleFont: "Anton",            sloganFont: "Inter" },
  { iconId: "infinity", layout: "icon-top",   titleFont: "Poppins",          sloganFont: "Poppins" },
];

// === PAGE: TEMPLATES (pick a starting template) ===
export function TemplatesPage() {
  const { title, slogan, setStyle, setIcon, setPage, referencePalette, onboarding,
          myTemplates, loadMyTemplate, deleteMyTemplate, renameMyTemplate } = useLogoStore();

  function pickMine(t: MyTemplate) {
    loadMyTemplate(t.id);
    setPage("editor");
  }
  function renderMine(t: MyTemplate) {
    return renderLogoSVG(templateToRenderState(t), { width: 500, height: 500 });
  }

  // Build palette-driven templates (only when an image was uploaded)
  const paletteTemplates: Template[] = (() => {
    if (referencePalette.length === 0) return [];
    const variations = generateVariations(referencePalette, PALETTE_SHELLS.length);
    return variations.map((v, i) => {
      const shell = PALETTE_SHELLS[i % PALETTE_SHELLS.length];
      return {
        id: `pal-${i}`,
        iconId: shell.iconId,
        layout: shell.layout,
        titleFont: shell.titleFont,
        sloganFont: shell.sloganFont,
        titleColor: v.titleColor,
        sloganColor: v.sloganColor,
        iconColor: v.iconColor,
        background: v.background,
      };
    });
  })();

  function pick(t: Template) {
    setIcon(t.iconId);
    setStyle({
      layout: t.layout,
      titleFont: t.titleFont,
      sloganFont: t.sloganFont,
      titleColor: t.titleColor,
      sloganColor: t.sloganColor,
      iconColor: t.iconColor,
      background: t.background,
      ...(t.containerShape         !== undefined && { containerShape:         t.containerShape }),
      ...(t.containerColor         !== undefined && { containerColor:         t.containerColor }),
      ...(t.containerColor2        !== undefined && { containerColor2:        t.containerColor2 }),
      ...(t.containerPattern       !== undefined && { containerPattern:       t.containerPattern }),
      ...(t.containerPatternColor  !== undefined && { containerPatternColor:  t.containerPatternColor }),
      ...(t.iconPattern            !== undefined && { iconPattern:            t.iconPattern }),
      ...(t.iconPatternColor       !== undefined && { iconPatternColor:       t.iconPatternColor }),
      ...(t.titleStyle3D           !== undefined && { titleStyle3D:           t.titleStyle3D }),
      ...(t.titleStroke3D          !== undefined && { titleStroke3D:          t.titleStroke3D }),
      ...(t.titleSkew              !== undefined && { titleSkew:              t.titleSkew }),
      ...(t.logoFrame              !== undefined && { logoFrame:              t.logoFrame }),
      ...(t.logoFrameColor         !== undefined && { logoFrameColor:         t.logoFrameColor }),
      ...(t.logoFrameBorderColor   !== undefined && { logoFrameBorderColor:   t.logoFrameBorderColor }),
      ...(t.logoFrameBorderWidth   !== undefined && { logoFrameBorderWidth:   t.logoFrameBorderWidth }),
    });
    setPage("editor");
  }

  function templateState(t: Template) {
    return {
      title: title || "Your Brand",
      slogan: slogan || "Tagline",
      iconId: t.iconId,
      qrText: null,
      layout: t.layout,
      titleFont: t.titleFont,
      sloganFont: t.sloganFont,
      titleColor: t.titleColor,
      sloganColor: t.sloganColor,
      iconColor: t.iconColor,
      background: t.background,
      titleSize: 56,
      sloganSize: 18,
      iconScale: 1,
      customIcons: [],
      // Optional comic-preset fields — feeding them to renderLogoSVG via Partial
      // makes the gallery thumbnail look identical to what the user gets after
      // clicking. Standard templates leave these undefined.
      containerShape:         t.containerShape,
      containerColor:         t.containerColor,
      containerColor2:        t.containerColor2,
      containerPattern:       t.containerPattern,
      containerPatternColor:  t.containerPatternColor,
      iconPattern:            t.iconPattern,
      iconPatternColor:       t.iconPatternColor,
      titleStyle3D:           t.titleStyle3D,
      titleStroke3D:          t.titleStroke3D,
      titleSkew:              t.titleSkew,
      logoFrame:              t.logoFrame,
      logoFrameColor:         t.logoFrameColor,
      logoFrameBorderColor:   t.logoFrameBorderColor,
      logoFrameBorderWidth:   t.logoFrameBorderWidth,
    };
  }
  function renderTemplate(t: Template) {
    return renderLogoSVG(templateState(t), { width: 400, height: 400 });
  }
  function scoreTemplate(t: Template) {
    return scoreLogo({ state: templateState(t) });
  }

  // Rank both the palette-derived templates and the standard templates by
  // aesthetic score + onboarding bonus (industry + color preferences).
  const rankedPalette = paletteTemplates
    .map((t) => {
      const s = scoreTemplate(t);
      const bonus = onboardingBonus(t, onboarding);
      return { t, s, ranked: s.score + bonus };
    })
    .sort((a, b) => b.ranked - a.ranked);
  const rankedStandard = TEMPLATES
    .map((t) => {
      const s = scoreTemplate(t);
      const bonus = onboardingBonus(t, onboarding);
      return { t, s, ranked: s.score + bonus };
    })
    .sort((a, b) => b.ranked - a.ranked);

  return (
    <div className="page templates">
      <PageLabel name="TEMPLATES" />
      <header className="topbar">
        <button className="btn pill-nav pill-nav-back" onClick={() => setPage("start")}>
          <span className="pill-nav-arrow" aria-hidden="true">←</span>
          <span>Back</span>
        </button>
        <h2>Logo ideas · {myTemplates.length > 0 ? `${myTemplates.length} saved + ` : ""}{rankedStandard.length} curated</h2>
        <button className="btn pill-nav pill-nav-fwd" onClick={() => setPage("editor")}>
          <span>Skip</span>
          <span className="pill-nav-arrow" aria-hidden="true">→</span>
        </button>
      </header>

      {myTemplates.length === 0 && (
        <div style={{
          margin: "16px 24px 0",
          padding: "14px 18px",
          background: "#f8fafc",
          border: "1px dashed #cbd5e1",
          borderRadius: 12,
          fontSize: 13,
          color: "#475569",
          lineHeight: 1.5,
        }}>
          💡 <strong>No saved logo ideas yet.</strong> Open the editor, design something, then click <strong>📌 Save as design</strong> in the top toolbar — saved designs appear here as starting-point suggestions for you and anyone else using this app.
        </div>
      )}

      {myTemplates.length > 0 && (
        <>
          <div className="section-head">
            <div>
              <h3>Saved logo ideas</h3>
              <p>Designs saved from the editor · pick one as a starting point and edit anything (name, colors, fonts) to make it yours.</p>
            </div>
          </div>
          <div className="my-designs-grid cols-3">
            {myTemplates.map((t) => (
              <div key={t.id} className="my-design-card">
                <button
                  type="button"
                  className="my-design-btn"
                  onClick={() => pickMine(t)}
                  title={`Use "${t.name}" as a starting point`}
                >
                  <div className="my-design-preview" dangerouslySetInnerHTML={{ __html: renderMine(t) }} />
                </button>
                <button
                  type="button"
                  className="my-design-delete"
                  title="Rename"
                  style={{ right: 40 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const name = prompt("Rename design:", t.name);
                    if (name !== null) renameMyTemplate(t.id, name);
                  }}
                >
                  ✎
                </button>
                <button
                  type="button"
                  className="my-design-delete"
                  title="Delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`Delete "${t.name}"? This can't be undone.`)) {
                      deleteMyTemplate(t.id);
                    }
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {paletteTemplates.length > 0 && (
        <>
          <div className="section-head">
            <div>
              <h3>Templates with your colors</h3>
              <p>Generated from your image · ranked by aesthetic score (Birkhoff + Wu).</p>
            </div>
            <div className="palette-strip">
              {referencePalette.map((c) => (
                <span key={c.hex} className="strip-chip" style={{ background: c.hex }} title={c.hex.toUpperCase()} />
              ))}
            </div>
          </div>
          <div className="template-grid">
            {rankedPalette.map(({ t, s }) => (
              <button key={t.id} className="template-card" onClick={() => pick(t)}>
                <div className="template-svg" dangerouslySetInnerHTML={{ __html: renderTemplate(t) }} />
                <div className={`template-score grade-${gradeClass(s.grade)}`}>{s.grade}<span> {s.scorePercent}</span></div>
              </button>
            ))}
          </div>
          <div className="section-head">
            <div>
              <h3>Standard templates</h3>
              <p>Hand-curated combinations · ranked by aesthetic score.</p>
            </div>
          </div>
        </>
      )}

      <div className="template-grid">
        {rankedStandard.map(({ t, s }) => (
          <button key={t.id} className="template-card" onClick={() => pick(t)}>
            <div className="template-svg" dangerouslySetInnerHTML={{ __html: renderTemplate(t) }} />
            <div className={`template-score grade-${gradeClass(s.grade)}`}>{s.grade}<span> {s.scorePercent}</span></div>
          </button>
        ))}
      </div>
    </div>
  );
}

function gradeClass(g: string): string {
  const c = g[0]?.toLowerCase() ?? "c";
  return c === "a" ? "a" : c === "b" ? "b" : c === "c" ? "c" : "d";
}
